import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Nav, NavItem } from '@feathr/components';
import { useGoogleAdsPermissions, useLicense } from '@feathr/extender/hooks';
import { useAccount, useLocalUrl } from '@feathr/extender/state';

function HomeMarketingNav(): JSX.Element {
  const { t } = useTranslation();
  const localUrl = useLocalUrl();

  const { isPlatformLite } = useAccount();
  const { googleAdsSmart, hasMonetization } = useLicense();
  // Google Ads is still flagged, so we need to check if the user has access to it.
  const showGoogleAds = useGoogleAdsPermissions();

  return (
    <Nav indented={true} label={t('Marketing navigation')} type={'vertical'}>
      <NavItem
        data-command={'true'}
        data-command-description={t('View all marketing campaigns')}
        data-command-keywords={'marketing,campaigns,all'}
        data-command-label={t('All Campaigns')}
        isForwardedFrom={localUrl(`/marketing`)}
        to={localUrl('/marketing/all')}
      >
        {t('All')}
      </NavItem>
      <NavItem
        data-command={'true'}
        data-command-description={t('Advertising campaigns')}
        data-command-keywords={'marketing,ads,advertising'}
        data-command-label={t('Ads')}
        to={localUrl('/marketing/ads')}
      >
        {t('Ads')}
      </NavItem>
      <NavItem
        data-command={'true'}
        data-command-description={t('Email campaigns')}
        data-command-keywords={'marketing,email,campaigns'}
        data-command-label={t('Email')}
        to={localUrl('/marketing/email')}
      >
        {t('Email')}
      </NavItem>
      {googleAdsSmart && showGoogleAds && (
        <NavItem
          data-command={'true'}
          data-command-description={t('Google Ads campaigns')}
          data-command-keywords={'marketing,google,ads,campaigns'}
          data-command-label={t('Google Ads')}
          to={localUrl('/marketing/google-ads')}
        >
          {t('Google Ads')}
        </NavItem>
      )}
      {hasMonetization && (
        <NavItem
          data-command={'true'}
          data-command-description={t('Monetization campaigns')}
          data-command-keywords={'marketing,monetization,campaigns'}
          data-command-label={t('Monetization')}
          to={localUrl('/marketing/monetization')}
        >
          {t('Monetization')}
        </NavItem>
      )}
      {!isPlatformLite && (
        <NavItem
          data-command={'true'}
          data-command-description={t('Other marketing campaigns')}
          data-command-keywords={'marketing,other,campaigns'}
          data-command-label={t('Other')}
          to={localUrl('/marketing/other')}
        >
          {t('Other')}
        </NavItem>
      )}
    </Nav>
  );
}

export default HomeMarketingNav;
